import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import React from 'react';

const useStyles = makeStyles(() => ({
  termsMainContainers: {
    maxWidth: '1000px',
    margin: 'auto',
    paddingBottom: '25px',
  },
  tableHead: {
    border: '1px solid',
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableCells: {
    border: '1px solid',
    textAlign: 'center',
  },
  list: {
    '& li::marker': {
      fontWeight: 'normal !important',
    },
  },
}));

function Ccpa() {
  const classes = useStyles();

  function createData(
    title: string,
    content: string,
    collect: string,
    disclose: string,
    sell: string,
    categories: string
  ) {
    return { title, content, collect, disclose, sell, categories };
  }

  const rows = [
    createData(
      'A. Identifiers Examples:',
      'Name, online identifier, internet protocol address, email address, or other similar identifiers.',
      'Yes',
      'Yes',
      'No',
      'I, II, III, IV, VI, VII, VIII'
    ),
    createData(
      'B. Categories of Personal Information in Cal. Civ. Code Section 1798.80(e)',
      'Examples: Name, education, employment, bank account number, credit card number, debit card number, or any other financial information.',
      'Yes',
      'Yes',
      'No',
      'All'
    ),
    createData(
      'C. Characteristics of Protected Classifications under California or Federal Law',
      'Examples: Race or color, ancestry or national origin, religion or creed, age (over 40), mental or physical disability, sex (including gender and pregnancy, childbirth, breastfeeding or related medical conditions), sexual orientation, gender identity or expression, medical condition, genetic information, marital status, military and veteran status.',
      'No',
      'N/A',
      'N/A',
      'N/A'
    ),
    createData(
      'D. Commercial Information',
      'Examples:, products or services purchased, obtained, or considered..',
      'Yes',
      'Yes',
      'No',
      'I, II, III, IV, VI, VII, VIII'
    ),
    createData(
      'E. Biometric Information',
      'Examples: Physiological, biological, or behavioral characteristics, including DNA, that can be used, singly or in combination with each other or with other identifying data, to establish individual identity, such as imagery of the iris, retina, fingerprint, face, hand, palm, vein patterns, and voice recordings, from which an identifier template, such as a faceprint, a minutiae template, or a voiceprint, can be extracted, and keystroke patterns or rhythms, gait patterns or rhythms, and sleep, health, or exercise data that contain identifying information.',
      'No',
      'N/A',
      'N/A',
      'N/A'
    ),
    createData(
      'F. Internet or Other Electronic Network Activity Information',
      'Examples: Browsing history, search history, and information regarding a consumer’s interaction with an internet website, application or advertisement.',
      'Yes',
      'Yes',
      'No',
      'I, II, VI, VII, VIII'
    ),
    createData(
      'G. Geolocation Data',
      'Example: Precise physical location.',
      'Yes',
      'Yes',
      'No',
      'I, II, IV, VI, VII, VIII'
    ),
    createData(
      'H. Sensory Information',
      'Examples: Audio, electronic, visual, thermal, olfactory, or similar information.',
      'No',
      'N/A',
      'N/A',
      'N/A'
    ),
    createData(
      'I. Professional or employment-related information',
      'Examples: Job application or resume information, past and current job history, and job performance information.',
      'Yes',
      'Yes',
      'No',
      'V, VI, VII, VIII'
    ),
    createData(
      'J. Non-Public Education Information (as defined in 20 U.S.C. 1232g; 34 C.F.R. Part 99)',
      'Examples: Records that are directly related to a student maintained by an educational agency or institution or by a party acting for the agency or institution.',
      'No',
      'N/A',
      'N/A',
      'N/A'
    ),
    createData(
      'K. Inferences Drawn from Personal Information',
      'Examples: Consumer profiles reflecting a consumer’s preferences, characteristics, psychological trends, predispositions, behavior, attitudes, intelligence, abilities, and aptitudes.',
      'Yes',
      'Yes',
      'No',
      'I, VI, VII, VIII'
    ),
  ];

  return (
    <div className="container">
      <div className={`${classes.termsMainContainers} terms-main-container`}>
        <Grid container justifyContent="center" className="page-header">
          <h1 className="pageTitle">
            QuillBot
            <br />
            CCPA Notice
          </h1>
        </Grid>
        <div className="terms-total-container qbp-pt20" style={{ textAlign: 'justify' }}>
          <p> Effective August 31, 2021</p>
          <p>
            This CCPA Notice supplements the information and disclosures contained in our{' '}
            <a href="/privacy">Privacy Policy</a>. It applies to individuals residing in California
            from whom we collect Personal Information as a business under the CCPA.
          </p>
          <h3>
            <u>Personal Information Collection, Disclosure, and Sale</u>
          </h3>
          <p>
            For the purposes of this notice, <b>“Personal Information”</b> means information that
            identifies, relates to, describes, is reasonably capable of being associated with, or
            could reasonably be linked, directly or indirectly, with a particular consumer or
            household, or as otherwise defined by the California Consumer Privacy Act of 2018
            (California Civil Code §§ 1798.100 to 1798.199) and its implementing regulations, as
            amended or superseded from time to time (<b>“CCPA”</b>).
            <p>Personal Information does not include information that is:</p>
            <ul>
              <li>Lawfully made available from government records.</li>
              <li>Deidentified or aggregated.</li>
              <li>Otherwise excluded from the scope of the CCPA.</li>
            </ul>
            <p>
              The chart below provides the categories of Personal Information (as defined by the
              CCPA) we have collected, disclosed for a business purpose, sold, or used for business
              or commercial purposes in the preceding twelve months since this notice was last
              updated, as well as the categories of third parties with whom we shared Personal
              Information. The examples of Personal Information provided for each category reflect
              each category’s statutory definition and may not reflect all of the specific types of
              Personal Information associated with each category.
            </p>
          </p>
          <TableContainer>
            <Table style={{ border: '1px solid', fontSize: '16px' }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHead} style={{ textAlign: 'left' }}>
                    Category
                  </TableCell>
                  <TableCell className={classes.tableHead}>We&nbsp;Collect</TableCell>
                  <TableCell className={classes.tableHead}>We&nbsp;Disclose</TableCell>
                  <TableCell className={classes.tableHead}>We&nbsp;Sell </TableCell>
                  <TableCell className={classes.tableHead}>
                    Categories of Third Parties with Whom We Share Personal Information
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.title} style={{ border: '1px solid' }}>
                    <TableCell
                      component="th"
                      scope="row"
                      className={classes.tableCells}
                      style={{ textAlign: 'left' }}
                    >
                      <p style={{ fontWeight: 'bold' }}>{row.title}</p>
                      {row.content}
                    </TableCell>

                    <TableCell className={classes.tableCells}>{row.collect}</TableCell>
                    <TableCell className={classes.tableCells}>{row.disclose}</TableCell>
                    <TableCell className={classes.tableCells}>{row.sell}</TableCell>
                    <TableCell className={classes.tableCells}>{row.categories}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <h3>
            <u>Use of Personal Information</u>
          </h3>
          <p>
            We collect, use, and disclose your Personal Information in accordance with the specific
            business and commercial purposes below:
            <ul style={{ listStyleType: 'disc' }}>
              <li>To provide, maintain, debug, improve, and enhance our Services;</li>
              <li>
                To understand and analyze how you use our Services, and develop new products,
                services, features, and functionalities;
              </li>
              <li>To understand and analyze usage patterns;</li>
              <li>To process your job application;</li>
              <li>To contact prospective customers through social medial and email;</li>
              <li>In de-identified and aggregated form to improve our Services;</li>
              <li>
                To communicate with you, provide you with updates and other information relating to
                our Services, provide information that you request, respond to comments and
                questions, and otherwise provide customer support;
              </li>
              <li>
                To find and prevent fraud, and respond to trust and safety issues that may arise;
              </li>
              <li>
                For compliance purposes, including enforcing our Terms of Service or other legal
                rights, or as may be required by applicable laws and regulations or requested by any
                judicial process or governmental agency; and
              </li>
              <li>
                For other purposes for which we provide specific notice at the time the Personal
                Information is collected.
              </li>
            </ul>
          </p>
          <h3>
            <u>Collection of Personal Information </u>
          </h3>
          <p>
            In the preceding twelve months since this notice was last updated, we have collected
            Personal Information from the following categories of sources:
            <ol className={classes.list}>
              <li>
                <b>You/Your Devices:</b> You or your devices directly.
              </li>
              <li>
                <b>Users:</b> Other users of our services.
              </li>
              <li>
                <b>Affiliates</b>.
              </li>
              <li>
                <b>Advertising Networks</b>.
              </li>
              <li>
                <b>ISPs:</b> Internet service providers.
              </li>
              <li>
                <b>Analytics Providers</b>.
              </li>
              <li>
                <b>OS/Platform Provider:</b> Operating systems and platforms.
              </li>
              <li>
                <b>Resellers:</b> Consumer data brokers.
              </li>
              <li>
                <b>Partners:</b> Business partners.
              </li>
              <li>
                <b>Public:</b> Publicly accessible sources.
              </li>
            </ol>
          </p>

          <h3>
            <u>Collection of Personal InformationDisclosure of Personal Information</u>
          </h3>
          <p>
            Pursuant to our Privacy Policy, we share your Personal Information with the following
            categories of third parties:
            <ol type="I" className={classes.list}>
              <li>
                <b>Analytics Providers</b>.
              </li>
              <li>
                <b>OS/Platform Provider: </b> Other users of our services.Operating systems and
                platforms.
              </li>
              <li>
                <b>Affiliates</b>.
              </li>
              <li>
                <b>Vendors: </b>Vendors and service providers.
              </li>
              <li>
                <b>Integrated Third Parties: </b> Third parties integrated into our services.
              </li>
              <li>
                <b>Third Parties as Legally Required: </b>Third parties as required by law and
                similar disclosures.
              </li>
              <li>
                <b>Third Parties in Merger/Acquisition: </b> Third parties in connection with a
                merger, sale, or asset transfer.
              </li>
              <li>
                <b>Third Parties with Consent:</b> Other third parties for whom we have obtained
                your permission to disclose your Personal Information.
              </li>
            </ol>
          </p>

          <h3>
            <u>Your California Privacy Rights</u>
          </h3>
          <p>If you are a California resident, you may exercise the following rights.</p>
          <p>
            <b>Right to Know and Access.</b> You may submit a verifiable request for information
            regarding the: (1) categories of Personal Information collected, sold, or disclosed by
            us; (2) purposes for which categories of Personal Information are collected or sold by
            us; (3) categories of sources from which we collect Personal Information; (4) categories
            of third parties with whom we disclosed or sold Personal Information; and (5) specific
            pieces of Personal Information we have collected about you during the past twelve
            months.
          </p>
          <p>
            <b>Right to Delete.</b> Subject to certain exceptions, you may submit a verifiable
            request that we delete Personal Information about you that we have collected from you.
            <ul style={{ listStyle: 'none' }}>
              <b>Verification.</b> Requests for access to or deletion of Personal Information are
              subject to our ability to reasonably verify your identity in light of the information
              requested and pursuant to relevant CCPA requirements, limitations, and regulations. To
              verify your access or deletion request, please: (1) authenticate your account by
              logging into your account or (2) provide us with your name and the email address under
              which your QuillBot account is registered.
            </ul>
          </p>

          <p>
            <b>Right to Opt Out.</b> In some circumstances, you may opt out of the sale of your
            Personal Information. The CCPA defines “sale” very broadly to cover common industry
            practices, including providing you with a customized experience on our websites and
            apps. We do not sell your Personal Information.
          </p>
          <p>
            <b>Right to Equal Service and Price.</b> You have the right not to receive
            discriminatory treatment for the exercise of your CCPA privacy rights, subject to
            certain limitations.
          </p>
          <p>
            <b>Shine the Light.</b> We do not rent, sell, or share Personal Information with
            nonaffiliated companies for their direct marketing uses as contemplated by California’s
            “Shine the Light” law (Civil Code § 1798.83), unless we have your permission.
          </p>
          <p>
            <b>Submit Requests.</b> To exercise your rights under the CCPA, please send an email to{' '}
            <a href="mailto:support@quillbot.com">support@quillbot.com</a> or visit our{' '}
            <a href="/contact">website</a>.
          </p>
          <p>
            <b>Authorizing an Agent.</b> If you are acting as an authorized agent to make a request
            to know, delete, or opt out on behalf of a California resident, please send an email via{' '}
            <a href="mailto:support@quillbot.com">support@quillbot.com</a> or visit our{' '}
            <a href="/contact">website</a> and attach a written authorization signed by the
            resident.
          </p>
          <h3>
            <u>Do Not Track</u>
          </h3>
          <p>
            There is no accepted standard on how to respond to Do Not Track signals, and we do not
            respond to such signals.
          </p>
          <h3>
            <u>Contact Information</u>
          </h3>
          <p>
            If you have any questions, comments, or concerns about this notice or our processing
            activities, or you would like to exercise your privacy rights, please email us at
            <a href="mailto:info@quillbot.com"> info@quillbot.com </a> or write to us at:
            <p>
              QuillBot (Course Hero), LLC
              <br />
              200 E. Randolph St. (Aon Center) Suite 5215
              <br />
              Chicago, Illinois 60601
            </p>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Ccpa;
